import { useState } from 'react';
import { useDealerAuth } from '../DealerAuthContext';
import { Lock, Eye, EyeOff, Shield, ChevronRight, Mail, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const BASE = process.env.REACT_APP_BACKEND_URL;

// ── Password Reset Form ────────────────────────────────────────────────────────
function ForgotPasswordForm({ onBack }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // TODO: Hook up real password reset endpoint when email is live
    await new Promise(r => setTimeout(r, 800)); // simulate
    setSent(true);
    setLoading(false);
  };

  if (sent) {
    return (
      <div className="text-center py-6">
        <CheckCircle size={40} className="text-orange-500 mx-auto mb-4" />
        <p className="font-bebas text-xl text-white tracking-wider mb-2">CHECK YOUR EMAIL</p>
        <p className="font-inter text-sm text-zinc-400 mb-6">
          If <span className="text-white">{email}</span> is registered, you'll receive a reset link shortly.
        </p>
        <button onClick={onBack} className="font-inter text-sm text-orange-500 hover:text-orange-400 transition-colors">
          ← Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 mb-2">
        <Mail size={15} className="text-orange-500" />
        <h2 className="font-bebas text-lg text-white tracking-wider">RESET PASSWORD</h2>
      </div>
      <p className="font-inter text-xs text-zinc-500 mb-5">
        Enter your dealer account email and we'll send a reset link.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-widest mb-1.5">
            Email Address
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors placeholder-zinc-600"
            placeholder="you@gunshop.com"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-lg tracking-widest py-3 transition-colors"
        >
          {loading ? 'SENDING...' : 'SEND RESET LINK'}
        </button>
        <button
          type="button"
          onClick={onBack}
          className="w-full font-inter text-xs text-zinc-500 hover:text-zinc-300 transition-colors py-1"
        >
          ← Back to Sign In
        </button>
      </form>
    </>
  );
}

// ── Main Dealer Login Page ─────────────────────────────────────────────────────
export default function DealerLogin() {
  const { login } = useDealerAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: '#0a0a0a' }}
      data-testid="dealer-login-page"
    >
      {/* Top bar */}
      <div className="border-b border-white/5 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield size={18} className="text-orange-500" />
          <span className="font-bebas text-lg text-white tracking-widest">WITT MACHINE</span>
          <span className="font-inter text-[10px] text-orange-500 border border-orange-500/30 px-2 py-0.5 uppercase tracking-widest">Dealer Portal</span>
        </div>
        <div className="font-inter text-[10px] text-zinc-700">
          wittmachine.com
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">

          {/* Hero headline */}
          <div className="text-center mb-8">
            <h1 className="font-bebas text-5xl text-white tracking-wider leading-none mb-2">
              WITT MACHINE
            </h1>
            <p className="font-bebas text-xl text-orange-500 tracking-widest">
              AUTHORIZED DEALER PORTAL
            </p>
            <p className="font-inter text-xs text-zinc-500 mt-2 leading-relaxed">
              Order wholesale inventory, track production status,<br />
              manage your FFL documentation — all in one place.
            </p>
          </div>

          {/* Card */}
          <div className="bg-[#111111] border border-white/10 p-7">

            {showReset ? (
              <ForgotPasswordForm onBack={() => setShowReset(false)} />
            ) : (
              <>
                <div className="flex items-center gap-2 mb-5">
                  <Lock size={14} className="text-orange-500" />
                  <h2 data-testid="dealer-login-heading" className="font-bebas text-lg text-white tracking-wider">
                    DEALER SIGN IN
                  </h2>
                </div>

                {error && (
                  <div
                    data-testid="dealer-login-error"
                    className="bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3 mb-5"
                  >
                    {error}
                  </div>
                )}

                <form data-testid="dealer-login-form" onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-widest mb-1.5">
                      Email Address
                    </label>
                    <input
                      data-testid="dealer-email-input"
                      type="email"
                      required
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors placeholder-zinc-600"
                      placeholder="you@gunshop.com"
                    />
                  </div>

                  <div>
                    <label className="block font-inter text-[10px] text-zinc-500 uppercase tracking-widest mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        data-testid="dealer-password-input"
                        type={showPwd ? 'text' : 'password'}
                        required
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 pr-10 outline-none transition-colors placeholder-zinc-600"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPwd(p => !p)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-white transition-colors"
                      >
                        {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    <div className="flex justify-end mt-1.5">
                      <button
                        type="button"
                        onClick={() => setShowReset(true)}
                        className="font-inter text-[10px] text-zinc-600 hover:text-orange-500 transition-colors"
                      >
                        Forgot password?
                      </button>
                    </div>
                  </div>

                  <button
                    data-testid="dealer-login-submit"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-xl tracking-widest py-4 transition-colors"
                  >
                    {loading ? 'SIGNING IN...' : 'SIGN IN →'}
                  </button>
                </form>
              </>
            )}
          </div>

          {/* Apply section */}
          <div className="mt-4 bg-[#111111] border border-white/10 p-5 flex items-center justify-between">
            <div>
              <p className="font-bebas text-sm text-white tracking-wider">NOT A DEALER YET?</p>
              <p className="font-inter text-[10px] text-zinc-500">Apply for wholesale access</p>
            </div>
            <Link
              to="/dealer-signup"
              data-testid="dealer-apply-link"
              className="flex items-center gap-1 font-inter text-xs text-orange-500 hover:text-orange-400 border border-orange-500/30 hover:border-orange-500 px-3 py-2 transition-colors"
            >
              Apply <ChevronRight size={12} />
            </Link>
          </div>

          {/* Support */}
          <div className="mt-4 text-center">
            <p className="font-inter text-[10px] text-zinc-700">
              Need help? Call <span className="text-zinc-600">(940) 461-8805</span>
            </p>
          </div>

          {/* Back to retail — for customers who landed here by mistake */}
          <div className="mt-6 border-t border-white/5 pt-5 text-center">
            <p className="font-inter text-[10px] text-zinc-600 mb-2">
              Looking to buy a suppressor or muzzle brake?
            </p>
            <a
              href="https://wittmachine.net"
              className="inline-flex items-center gap-1.5 font-inter text-xs text-zinc-500 hover:text-white border border-white/10 hover:border-white/30 px-4 py-2 transition-colors"
            >
              ← Visit the Witt Machine Store
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/5 px-6 py-4 text-center">
        <p className="font-inter text-[10px] text-zinc-800">
          © 2026 Witt Machine &amp; Tool Co. — Texas, USA &nbsp;·&nbsp; SOT/Class III NFA Manufacturer
        </p>
      </div>
    </div>
  );
}
